import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Toast notification types
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info'

/**
 * Toast notification interface
 */
export interface Toast {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number
  dismissible?: boolean
}

/**
 * Notifications Store (Pinia)
 * Manages toast notifications and alerts
 */
export const useNotificationsStore = defineStore('notifications', () => {
  // State
  const toasts = ref<Toast[]>([])
  const nextId = ref(1)

  // Actions

  /**
   * Add a toast notification
   */
  function addToast(
    type: ToastType,
    title: string,
    message?: string,
    duration: number = 5000,
    dismissible: boolean = true
  ): string {
    const id = `toast-${nextId.value++}`
    const toast: Toast = {
      id,
      type,
      title,
      message,
      duration,
      dismissible
    }

    toasts.value.push(toast)

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }

    return id
  }

  /**
   * Remove a toast by ID
   */
  function removeToast(id: string) {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  /**
   * Clear all toasts
   */
  function clearAllToasts() {
    toasts.value = []
  }

  /**
   * Convenience methods for different toast types
   */
  function success(title: string, message?: string, duration?: number) {
    return addToast('success', title, message, duration)
  }

  function error(title: string, message?: string, duration?: number) {
    return addToast('error', title, message, duration || 8000) // Errors stay longer
  }

  function warning(title: string, message?: string, duration?: number) {
    return addToast('warning', title, message, duration)
  }

  function info(title: string, message?: string, duration?: number) {
    return addToast('info', title, message, duration)
  }

  return {
    // State
    toasts,

    // Actions
    addToast,
    removeToast,
    clearAllToasts,
    success,
    error,
    warning,
    info
  }
})
